import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useToasterContext } from '@src/context/Toaster';
import { AlertType } from '@src/lib/types/toaster';
import Config from "@src/utils/api/config";
import React from 'react';
import { useService } from '../hooks';
import { useRaffleDrawContext } from '../provider';
import Input from './input';
import NewMailMask from './new-mail-mask';
import { useCreateContext } from './provider';

import Iconify from '@src/components/iconify';
import {
  StyledContainer,
  StyledStatus,
  StyledUploadBox,
  styles
} from './styles.css';

const PLAYER_TYPES = [
  {
    value: "REGULAR",
    label: "Regular Players",
    description: "Open to all eligible regular players.",
    icon: "mdi:account-group-outline",
  },
  {
    value: "VIP",
    label: "VIP Players",
    description: "Exclusive for VIP members only.",
    icon: "mdi:crown-outline",
  },
];

function MailDescription() {
  const { uploadImage } = useService();
  const { triggerOpen } = useToasterContext();

  const { openCreate } = useRaffleDrawContext();
  const { state, error, onDispatch } = useCreateContext();

  const handleFileChange = (type: string) => (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) {
        triggerOpen({
          type: AlertType.error,
          title: "Failed",
          message: "File size must be less than 3MB",
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);
      uploadImage.mutate(formData);
    }
  };

  React.useEffect(() => {
    if(uploadImage.data && uploadImage.isSuccess){
      onDispatch(uploadImage.data.type)(`${Config.APP_CDN_URL}/${uploadImage.data.result.filePath}`);
    }
  },[uploadImage.isSuccess, uploadImage.data, onDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledContainer>
      <Button sx={styles.btn_close} onClick={openCreate}>
        <Iconify icon={'mdi:close'} width={25} />
      </Button>

      {/* Draw # and Status row */}
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={'center'} mt={3}>
        <Typography display="inline" variant="body1" sx={styles.t_status}>
          Draw #:
          <Typography component="span" variant="body1" fontWeight={"700"}>
            {state.code}
          </Typography>
        </Typography>
        <StyledStatus>
          <Typography display="inline" variant="body1" sx={styles.t_status}>
            Status:
            <Typography component="span" variant="body1" fontWeight={"700"}
              color={state?.status === "SCHEDULED" ? '#44B370' : "#A2A2A2"}>
              {state?.status?.replace("DEACTIVATED", "Inactive/Draft")}
            </Typography>
          </Typography>
          <Button
            variant="text"
            onClick={() => onDispatch("status")(state.status === "SCHEDULED" ? "INACTIVE" : "SCHEDULED")}
            sx={[styles.btn_deactivation, {bgcolor: state.status === "SCHEDULED" ? '#484848' : "#5EDB7B"}]}
          >
            {state.status === "SCHEDULED" ? "INACTIVE" : "ACTIVATE DRAW"}
          </Button>
        </StyledStatus>
      </Stack>

      {/* Type + Upload row */}
      <Stack
        direction={{ xl: "row", lg: "row", md: "row", sm: "column", xs: "column" }}
        gap={2}
        mt={3}
        alignItems="flex-start"
      >
        {/* Type selector */}
        <Stack flex={1} minWidth={0}>
          <Typography fontSize={14} fontWeight={600} color="#0F172A" mb={1.5}>
            Type <Typography component="span" color="error">*</Typography>
          </Typography>
          <Stack direction="column" gap={1.5}>
            {PLAYER_TYPES.map((pt) => {
              const isSelected = state.subject === pt.value;
              return (
                <Box
                  key={pt.value}
                  onClick={() => onDispatch("subject")(pt.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: "12px",
                    border: isSelected ? "1.5px solid #6A00AC" : "1.5px solid #E5E7EF",
                    bgcolor: isSelected ? "#F7F0FF" : "#FAFAFA",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    position: "relative",
                  }}
                >
                  <Box sx={{
                    width: 40, height: 40,
                    borderRadius: "50%",
                    bgcolor: isSelected ? "#EDE8FF" : "#F0F0F0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Iconify
                      icon={pt.icon}
                      width={22}
                      sx={{ color: isSelected ? "#6A00AC" : "#888" }}
                    />
                  </Box>
                  <Stack>
                    <Typography fontSize={14} fontWeight={700} color={isSelected ? "#0F172A" : "#555"}>
                      {pt.label}
                    </Typography>
                    <Typography fontSize={12} color={isSelected ? "#555" : "#999"}>
                      {pt.description}
                    </Typography>
                  </Stack>
                  {isSelected && (
                    <Box sx={{
                      position: "absolute", right: 14,
                      width: 20, height: 20, borderRadius: "50%",
                      bgcolor: "#6A00AC",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Iconify icon="mdi:check" width={13} sx={{ color: "white" }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Stack>

        {/* Upload thumbnail */}
        <Stack gap={1} flex={1} minWidth={0}>
          <Typography fontSize={14} fontWeight={600} color="#0F172A">Upload Thumbnail</Typography>
          <StyledUploadBox sx={[
            {
              width: "100%",
              bgcolor: "#FAFAFA",
              border: "1.5px dashed #CBD5E1",
              borderRadius: "12px",
              position: "relative",
              overflow: "hidden",
            },
            error.thumbnail && { border: "1.5px dashed red" }
          ]}>
            <input
              type="file"
              id="thumbnail"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange("thumbnail")}
            />
            {!state.thumbnail && <NewMailMask
              notes="Recommended size: 400×300px"
              notes2="JPG, PNG, max: 2mb"
            />}
            {uploadImage.isPending &&
              <CircularProgress sx={{ position: "absolute" }} />}
            {state.thumbnail &&
              <img
                src={state.thumbnail}
                alt="Selected"
                style={{ maxWidth: '90%', height: '90%', objectFit: "cover", borderRadius: 8 }} />}
          </StyledUploadBox>
          <Button
            disabled={uploadImage.isPending}
            onClick={() => document.getElementById('thumbnail')?.click()}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: "#484848",
              color: "white",
              borderRadius: "8px",
              height: 44,
              fontWeight: 700,
              fontSize: 13,
              "&:hover": { bgcolor: "#2e2e40" },
            }}
          >
            UPLOAD THUMBNAIL
          </Button>
        </Stack>
      </Stack>

      {/* Short description — full width below */}
      <Stack mt={2}>
        <Input
          sx={{ mt: 0 }}
          label="Short Description"
          labelSx={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}
          inputSx={{
            '& .MuiInputBase-root': {
              width: "auto",
              height: '120px',
              borderRadius: "10px",
              bgcolor: "#FAFAFA",
            },
            "& .MuiOutlinedInput-root": {
              alignItems: "flex-start",
              padding: "12px 14px",
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              lineHeight: "22px",
              padding: 0,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#CBD5E1",
            },
          }}
          placeholder='Enter short description'
          error={error.description}
          value={state.description}
          onChange={onDispatch("description")}
          maxLength={150}
          showCount
        />
      </Stack>
    </StyledContainer>
  );
}

export default MailDescription;
